'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
  RiArrowDownSLine,
  RiArrowLeftSLine,
  RiArrowRightSLine,
  RiCalendarLine,
  RiCloseLine,
} from '@remixicon/react';
import { cn } from '@/design-system/lib/utils';
import { Button } from '@/components/Button';

// Enable dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);

// Types
interface DateRange {
  from: Date | null;
  to: Date | null;
}

interface TimeRangeOption {
  label: string;
  value: string;
}

interface DateRangeFilterProps {
  onDateRangeChange?: (range: DateRange) => void;
  className?: string;
  initialRange?: DateRange;
}

// Time range options
const timeRangeOptions: TimeRangeOption[] = [
  { label: 'Today', value: 'today' },
  { label: 'Last 7 days', value: 'last-7-days' },
  { label: 'Last 30 days', value: 'last-30-days' },
  { label: 'Last 3 Months', value: 'last-3-months' },
  { label: 'All Time', value: 'all-time' },
  { label: 'Custom', value: 'custom' },
];

// Kit-token styling for the split-control trigger buttons
const triggerButtonClasses = cn(
  'flex items-center gap-2 px-3 py-2 border border-stroke-soft-200 bg-bg-white-0',
  'hover:bg-bg-weak-50 transition-colors',
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
  'text-sm tracking-tight font-medium text-text-strong-950'
);

const dateInputClasses = cn(
  'w-full h-10 px-3 py-2 text-sm border border-stroke-soft-200 rounded-lg',
  'bg-bg-white-0 text-text-strong-950 transition-colors',
  'focus:outline-none focus:border-stroke-strong-950 focus:ring-2 focus:ring-ring focus:ring-offset-2'
);

// Calendar component using HTML5 date inputs
const Calendar: React.FC<{
  range: DateRange;
  onRangeChange: (range: DateRange) => void;
  currentMonth: Date;
  onMonthChange: (month: Date) => void;
  showDualCalendar?: boolean;
}> = ({
  range,
  onRangeChange,
  currentMonth,
  onMonthChange,
  showDualCalendar = true,
}) => {
  const handleStartDateChange = (value: string) => {
    if (value) {
      const parts = value.split('-');
      if (parts[0].length > 4) {
        parts[0] = parts[0].slice(-4);
        value = parts.join('-');
      }
    }
    const newDate = value ? new Date(value) : null;
    onRangeChange({ from: newDate, to: range.to });
  };

  const handleEndDateChange = (value: string) => {
    if (value) {
      const parts = value.split('-');
      if (parts[0].length > 4) {
        parts[0] = parts[0].slice(-4);
        value = parts.join('-');
      }
    }
    const newDate = value ? new Date(value) : null;
    onRangeChange({ from: range.from, to: newDate });
  };

  const formatDateForInput = (date: Date | null): string => {
    if (!date) return '';
    return dayjs(date).format('YYYY-MM-DD');
  };

  const handlePrevMonth = () => {
    const prevMonth = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() - 1,
      1
    );
    onMonthChange(prevMonth);
  };

  const handleNextMonth = () => {
    const nextMonth = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + 1,
      1
    );
    onMonthChange(nextMonth);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Month Navigation */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-stroke-soft-200">
        <button
          onClick={handlePrevMonth}
          aria-label="Previous month"
          className="p-1 rounded-md text-icon-sub-600 hover:bg-bg-weak-50 transition-colors"
        >
          <RiArrowLeftSLine aria-hidden className="size-5" />
        </button>

        <span className="text-sm font-medium text-text-strong-950">
          {dayjs(currentMonth).format('MMMM YYYY')}
        </span>

        <button
          onClick={handleNextMonth}
          aria-label="Next month"
          className="p-1 rounded-md text-icon-sub-600 hover:bg-bg-weak-50 transition-colors"
        >
          <RiArrowRightSLine aria-hidden className="size-5" />
        </button>
      </div>

      {/* Date Range Inputs */}
      <div className="flex-1 p-4 space-y-4">
        <div>
          <label className="block mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-text-sub-600">
            Start Date
          </label>
          <input
            type="date"
            max="9999-12-31"
            value={formatDateForInput(range.from)}
            onChange={(e) => handleStartDateChange(e.target.value)}
            className={dateInputClasses}
          />
        </div>

        <div>
          <label className="block mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-text-sub-600">
            End Date
          </label>
          <input
            type="date"
            max="9999-12-31"
            value={formatDateForInput(range.to)}
            onChange={(e) => handleEndDateChange(e.target.value)}
            min={formatDateForInput(range.from)}
            className={dateInputClasses}
          />
        </div>

        {/* Quick Calendar View */}
        <div className="mt-6">
          <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-text-sub-600">
            Current Month
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-xs">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
              <div
                key={day}
                className="font-semibold uppercase tracking-[0.16em] text-[10px] text-text-soft-400 p-2"
              >
                {day}
              </div>
            ))}
            {Array.from(
              { length: dayjs(currentMonth).daysInMonth() },
              (_, i) => {
                const date = dayjs(currentMonth)
                  .date(i + 1)
                  .toDate();
                const isInRange =
                  range.from &&
                  range.to &&
                  date >= range.from &&
                  date <= range.to;
                const isStart =
                  range.from && dayjs(date).isSame(range.from, 'day');
                const isEnd = range.to && dayjs(date).isSame(range.to, 'day');

                return (
                  <button
                    key={i}
                    onClick={() => {
                      if (!range.from || (range.from && range.to)) {
                        onRangeChange({ from: date, to: null });
                      } else {
                        onRangeChange({
                          from: range.from,
                          to: date >= range.from ? date : range.from,
                        });
                        if (date < range.from) {
                          onRangeChange({ from: date, to: range.from });
                        }
                      }
                    }}
                    className={cn(
                      'p-2 text-xs tabular-nums rounded-lg transition-colors',
                      (isStart || isEnd) &&
                        'bg-bg-strong-950 text-text-white-0 font-semibold',
                      isInRange &&
                        !isStart &&
                        !isEnd &&
                        'bg-(--theme-accent-alpha-10) text-accent font-medium',
                      !isInRange &&
                        !isStart &&
                        !isEnd &&
                        'text-text-sub-600 hover:bg-bg-weak-50'
                    )}
                  >
                    {i + 1}
                  </button>
                );
              }
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  onDateRangeChange,
  className = '',
  initialRange = { from: null, to: null },
}) => {
  const [range, setRange] = useState<DateRange>(initialRange);
  const [selectedTimeRange, setSelectedTimeRange] =
    useState<string>('all-time');
  const [isTimeRangeOpen, setIsTimeRangeOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const timeRangeRef = useRef<HTMLDivElement>(null);
  const datePickerRef = useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        timeRangeRef.current &&
        !timeRangeRef.current.contains(event.target as Node)
      ) {
        setIsTimeRangeOpen(false);
      }
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target as Node)
      ) {
        setIsDatePickerOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Removed automatic range change notification - only on Apply

  const getDateRangeForTimeOption = (option: string): DateRange => {
    // Helper function to create a local date at start of day
    const createLocalDate = (daysFromToday: number = 0): Date => {
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth();
      const day = now.getDate() + daysFromToday;

      // Create a new date with the calculated day, ensuring local timezone
      const date = new Date(year, month, day, 0, 0, 0, 0);
      return date;
    };

    switch (option) {
      case 'today':
        return {
          from: createLocalDate(0),
          to: createLocalDate(0),
        };
      case 'last-7-days':
        return {
          from: createLocalDate(-7),
          to: createLocalDate(0),
        };
      case 'last-30-days':
        return {
          from: createLocalDate(-30),
          to: createLocalDate(0),
        };
      case 'last-3-months':
        return {
          from: dayjs().subtract(3, 'month').startOf('day').toDate(),
          to: createLocalDate(0),
        };
      case 'all-time':
        return { from: null, to: null };
      default:
        return { from: null, to: null };
    }
  };

  const handleTimeRangeSelect = (option: string) => {
    setSelectedTimeRange(option);
    setIsTimeRangeOpen(false);

    if (option === 'all-time') {
      const newRange = { from: null, to: null };
      setRange(newRange);
      onDateRangeChange?.(newRange);
      return;
    }

    setIsDatePickerOpen(true);

    if (option !== 'custom') {
      const newRange = getDateRangeForTimeOption(option);
      setRange(newRange);

      // Update calendar view to show the start date of the selected range
      if (newRange.from) {
        setCurrentMonth(newRange.from);
      } else {
        setCurrentMonth(new Date());
      }
    }
  };

  const handleDateRangeChange = useCallback((newRange: DateRange) => {
    setRange(newRange);
    // Reset time range selection when manually selecting dates
    if (newRange.from || newRange.to) {
      setSelectedTimeRange('custom');
    }
  }, []);

  const handleApplyDateRange = () => {
    setIsDatePickerOpen(false);
    onDateRangeChange?.(range);
  };

  const handleClearDateRange = () => {
    const clearedRange = { from: null, to: null };
    setRange(clearedRange);
    setSelectedTimeRange('all-time');
    onDateRangeChange?.(clearedRange);
  };

  const formatDateRange = (range: DateRange): string => {
    if (!range.from) return 'Select date range';

    if (!range.to) {
      return dayjs(range.from).format('MMM DD, YYYY');
    }

    if (dayjs(range.from).isSame(range.to, 'day')) {
      return dayjs(range.from).format('MMM DD, YYYY');
    }

    return `${dayjs(range.from).format('MMM DD')} - ${dayjs(range.to).format(
      'MMM DD, YYYY'
    )}`;
  };

  const getSelectedTimeRangeLabel = () => {
    const option = timeRangeOptions.find(
      (opt) => opt.value === selectedTimeRange
    );
    return option?.label || 'All Time';
  };

  return (
    <div className={cn('inline-flex', className)}>
      {/* Time Range Dropdown */}
      <div className="relative" ref={timeRangeRef}>
        <button
          onClick={() => setIsTimeRangeOpen(!isTimeRangeOpen)}
          className={cn(triggerButtonClasses, 'rounded-l-lg')}
        >
          <span className="truncate max-w-32">
            {getSelectedTimeRangeLabel()}
          </span>
          <RiArrowDownSLine
            aria-hidden
            className={cn(
              'size-4 shrink-0 text-icon-sub-600 transition-transform',
              isTimeRangeOpen && 'rotate-180'
            )}
          />
        </button>

        {isTimeRangeOpen && (
          <div className="absolute z-50 mt-1 w-48 bg-bg-white-0 border border-stroke-soft-200 rounded-lg shadow-custom-shadows-medium overflow-hidden">
            {timeRangeOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => handleTimeRangeSelect(option.value)}
                className={cn(
                  'w-full text-left px-3 py-2 text-sm transition-colors border-b border-stroke-soft-200 last:border-b-0',
                  selectedTimeRange === option.value
                    ? 'bg-(--theme-accent-alpha-10) text-accent font-medium'
                    : 'text-text-sub-600 hover:bg-bg-weak-50'
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Date Picker Button */}
      <div className="relative" ref={datePickerRef}>
        <button
          onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
          className={cn(triggerButtonClasses, 'border-l-0 rounded-r-lg')}
        >
          <RiCalendarLine
            aria-hidden
            className="size-4 shrink-0 text-icon-sub-600"
          />
          <span className="truncate max-w-48">
            {selectedTimeRange === 'custom'
              ? formatDateRange(range)
              : 'Custom Range'}
          </span>
        </button>

        {isDatePickerOpen && (
          <div
            className="absolute z-50 mt-1 bg-bg-white-0 border border-stroke-soft-200 rounded-lg shadow-custom-shadows-medium overflow-hidden"
            style={{ width: '400px', minHeight: '610px' }}
          >
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-stroke-soft-200 bg-bg-weak-50">
                <h3 className="text-[10px] font-semibold uppercase tracking-[0.16em] text-text-strong-950">
                  Select Date Range
                </h3>
                <button
                  onClick={() => setIsDatePickerOpen(false)}
                  aria-label="Close"
                  className="text-icon-soft-400 hover:text-text-sub-600 transition-colors"
                >
                  <RiCloseLine aria-hidden className="size-4" />
                </button>
              </div>

              {/* Calendar */}
              <Calendar
                range={range}
                onRangeChange={handleDateRangeChange}
                currentMonth={currentMonth}
                onMonthChange={setCurrentMonth}
              />

              {/* Footer */}
              <div className="flex items-center justify-between px-4 py-3 border-t border-stroke-soft-200 bg-bg-weak-50">
                <p className="text-sm text-text-sub-600 tabular-nums">
                  {range.from && range.to
                    ? `${dayjs(range.from).format('MMM DD, YYYY')} - ${dayjs(
                        range.to
                      ).format('MMM DD, YYYY')}`
                    : range.from
                      ? dayjs(range.from).format('MMM DD, YYYY')
                      : 'No range selected'}
                </p>
                <div className="flex gap-2">
                  <Button
                    size="small"
                    variant="neutral"
                    onClick={handleClearDateRange}
                  >
                    Clear
                  </Button>
                  <Button
                    size="small"
                    variant="primary"
                    onClick={handleApplyDateRange}
                    disabled={!range.from}
                  >
                    Apply
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DateRangeFilter;
